import { gql, useQuery } from "@apollo/client";
import type { ICustomerOrdersQuery, IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import { customerOrdersQuery } from "@repo/shopify/src/queries/customer-gql";
import { orderHistoryFilter } from "@repo/shopify/src/utils/order";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import useUser from "@hooks/reducerHooks/useUser";

import { PREOPEN_DATETIME } from "@utils/archive-sale";

const useOrders = (option?: { observerDisabled?: boolean }) => {
  const { pathname } = useLocation();
  const { accessToken } = useUser();

  const loaderEl = useRef(null);
  const firstIntersecting = useRef(true);

  const variables = {
    customerAccessToken: accessToken,
    first: 100, // 아카이브 세일 페이지에서 한 번에 100개씩 불러오기
    sortKey: "PROCESSED_AT",
    reverse: true,
  };

  const { data, loading, fetchMore, refetch } = useQuery<ICustomerOrdersQuery>(gql(customerOrdersQuery.query), {
    variables,
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      console.log(error);
    },
    skip: option?.observerDisabled,
  });

  const hasNextPage = useMemo(
    () => data?.customer?.orders?.pageInfo.hasNextPage || false,
    [data?.customer?.orders?.pageInfo],
  );
  const endCursor = useMemo(() => data?.customer?.orders?.pageInfo.endCursor || "", [data?.customer?.orders?.pageInfo]);

  /**
   * InterSectionObserver handler
   * 주문 데이터 추가 쿼리
   * */
  const handleObserver = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      if (loading || !hasNextPage) return;

      const target = entries[0];

      if (target.isIntersecting) {
        if (!firstIntersecting.current) {
          firstIntersecting.current = false;
        }

        await fetchMore({
          variables: { ...variables, after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            return {
              customer: {
                ...prev.customer,
                orders: {
                  ...prev.customer?.orders,
                  edges: [...(prev.customer?.orders?.edges || []), ...(fetchMoreResult.customer?.orders?.edges || [])],
                  pageInfo: fetchMoreResult.customer.orders.pageInfo,
                },
              },
            };
          },
        });
      }
    },
    [hasNextPage, loading, endCursor],
  );

  useEffect(() => {
    if (option?.observerDisabled) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "1000px 0px 0px 0px",
      threshold: 0.1,
    });

    if (loaderEl?.current) observer.observe(loaderEl.current);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, loaderEl]);

  // 아카이브 세일 주문 히스토리 필터링
  const archiveSaleOrders = data?.customer.orders.edges
    .filter(({ node }: { node: IOrdersEdgesNode }) => orderHistoryFilter(node))
    .filter(({ node }: { node: IOrdersEdgesNode }) => node.isfamilysale)
    .filter(({ node }: { node: IOrdersEdgesNode }) => {
      const processedAt = new Date(node.processedAt);
      return new Date(PREOPEN_DATETIME) < processedAt;
    });

  return {
    data,
    loading,
    refetch,
    hasNextPage,
    loaderEl,
    orders: archiveSaleOrders || [],
    drawOrders:
      data?.customer.orders.edges.filter(({ node }: { node: IOrdersEdgesNode }) => Boolean(node.instagram)) || [],
    firstIntersecting,
  };
};

export default useOrders;
